import { useMutation, useQuery } from '@tanstack/react-query'
import { type FC, useCallback, useMemo, useState } from 'react'

import type { ResPrice } from '@/lib/queries/business/pricing/types'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'
import { type ResUserQuery } from '@/lib/queries/business/v2/user/types'
import { createCheckoutSessionMutation } from '@/lib/queries/payment/v1/subscription/stripe/checkout/mutations'
import { createCustomerPortalSessionMutation } from '@/lib/queries/payment/v1/subscription/stripe/portal/mutations'
import { cn } from '@/lib/utils'

import { PricingPlan } from './pricing-plan'

export interface PricingPlansProps {
  user: ResUserQuery
  pricing?: ResPrice[]
}

export const PricingPlans: FC<PricingPlansProps> = ({ user, pricing }) => {
  const [isPending, setIsPending] = useState(false)

  const pricingTable = useMemo(() => {
    const basic = pricing?.find((x) => x.product_name.match(/basic/i))
    const premium = pricing?.find((x) => x.product_name.match(/premium/i))
    const growth = pricing?.find((x) => x.product_name.match(/growth/i))
    const enterprise = pricing?.find((x) => x.product_name.match(/enterprise/i))

    return {
      basic,
      premium,
      growth,
      enterprise,
    }
  }, [pricing])

  const { data: dataUser } = useQuery(getUserQuery)

  const { mutateAsync: openNewSubscribeSession } = useMutation(createCheckoutSessionMutation)
  const { mutateAsync: openSubscribeManagingSession } = useMutation(createCustomerPortalSessionMutation)

  const handleSelect = useCallback(
    async (priceId: string) => {
      setIsPending(true)

      if (dataUser?.subscription?.customerId) {
        await openSubscribeManagingSession(
          {
            customerId: dataUser.subscription.customerId,
            returnUrl: `${window.location.origin}/settings`,
          },
          {
            onSuccess: (data) => {
              window.location.href = data
            },
            onError: (error) => {
              console.error('Error when trying to open Stripe page(existing subscription): ', error)
            },
          }
        )
      } else {
        await openNewSubscribeSession(
          {
            priceId,
            successUrl: `${window.location.origin}/settings?subscription=success`,
            cancelUrl: `${window.location.origin}/settings`,
          },
          {
            onSuccess: (data) => {
              window.location.href = data
            },
            onError: (error) => {
              console.error('Error when trying to open Stripe page(new subscription): ', error)
            },
          }
        )
      }

      setIsPending(false)
    },
    [dataUser, openNewSubscribeSession, openSubscribeManagingSession]
  )

  return (
    <div
      className={cn(
        'mb-10 flex animate-fade-in flex-col justify-center gap-[30px] px-5 duration-150 lg:flex-row',
        isPending && 'opacity-90'
      )}
    >
      {!!pricingTable.basic && (
        <PricingPlan
          name="Basic"
          priceId={pricingTable.basic.price_id}
          price={pricingTable.basic.price}
          nCases={pricingTable.basic.max_cases}
          freeTrialAvailable={user.freeTrialAvailable}
          disabled={isPending}
          onSelect={handleSelect}
        />
      )}
      {!!pricingTable.premium && (
        <PricingPlan
          name="Premium"
          priceId={pricingTable.premium.price_id}
          price={pricingTable.premium.price}
          nCases={pricingTable.premium.max_cases}
          freeTrialAvailable={user.freeTrialAvailable}
          disabled={isPending}
          onSelect={handleSelect}
        />
      )}
      {!!pricingTable.growth && (
        <PricingPlan
          name="Growth"
          priceId={pricingTable.growth.price_id}
          price={pricingTable.growth.price}
          nCases={pricingTable.growth.max_cases}
          freeTrialAvailable={user.freeTrialAvailable}
          disabled={isPending}
          onSelect={handleSelect}
        />
      )}
    </div>
  )
}
